import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleRight, UserX, UserCheck, UserPlus, Users, Search, Crown, Star, User, Shield, AlertCircle, CheckCircle, Clock, Zap, Mail, Save, X } from 'lucide-react';
import { usersAPI } from '../services/api';
import { hasPermission, loadStoredPermissions, PERMISSION_KEYS } from '../utils/permissions';

const USER_ROLES = [
  { value: 'admin', labelKey: 'common.roleAdministrator' },
  { value: 'operator', labelKey: 'common.roleOperation' },
  { value: 'viewer', labelKey: 'common.roleViewer' },
];

const emptyUser = { username: '', email: '', full_name: '', role: 'viewer', password: '', is_active: true };

const normalizeUser = (u) => ({
  ...u,
  username: u.username || '',
  email: u.email || '',
  role: u.role || 'viewer',
  is_active: u.is_active === true || u.is_active === 1 || u.is_active === '1',
  last_login: u.last_login || null,
  created_at: u.created_at || null,
  login_attempts: Number(u.login_attempts) || 0,
});

const ManagementAccountStatus = ({ userRole, userPermissions = [] }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const role = (userRole || localStorage.getItem('userRole') || 'user').toLowerCase();
  const permissions = userPermissions.length ? userPermissions : loadStoredPermissions();
  const canManageUsers = hasPermission(permissions, PERMISSION_KEYS.MANAGEMENT_USER_PROFILE);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await usersAPI.list({ page: 1, limit: 200 });
      const data = res?.data || {};
      setUsers((data.users || []).map(normalizeUser));
    } catch (e) {
      setError(e?.message || t('managementUserProfile.loadError'));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const toggle = async (id) => {
    const current = users.find(u => u.id === id);
    if (!current) return;
    const nextActive = !current.is_active;
    setUsers(prev => prev.map(u => u.id === id ? { ...u, is_active: nextActive } : u));
    try {
      await usersAPI.update(id, { is_active: nextActive });
    } catch (e) {
      setUsers(prev => prev.map(u => u.id === id ? { ...u, is_active: current.is_active } : u));
      setError(e?.message || t('managementAccountStatus.updateError'));
    }
  };

  const openCreate = () => { setError(null); setForm(emptyUser); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setForm(emptyUser); };

  const createUser = async () => {
    if (!canManageUsers) {
      setError(t('managementAccountStatus.noPermission'));
      return;
    }
    if (!form.username.trim()) {
      setError(t('managementAccountStatus.usernameRequired'));
      return;
    }
    if (!form.password) {
      setError(t('managementAccountStatus.passwordRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await usersAPI.create({ ...form, username: form.username.trim() });
      closeForm();
      await loadUsers();
    } catch (e) {
      setError(e?.message || t('managementAccountStatus.createError'));
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (userRoleName) => {
    const found = USER_ROLES.find(r => r.value === (userRoleName || '').toLowerCase());
    return found ? t(found.labelKey) : (userRoleName || t('common.unknown'));
  };

  const getRoleIcon = (userRoleName) => {
    switch((userRoleName || '').toLowerCase()) {
      case 'admin': return <Crown className="h-4 w-4 text-red-600" />;
      case 'operator': return <Star className="h-4 w-4 text-blue-600" />;
      case 'viewer': return <User className="h-4 w-4 text-green-600" />;
      default: return <User className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRoleBadgeColor = (userRoleName) => {
    switch((userRoleName || '').toLowerCase()) {
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'operator': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'viewer': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return t('managementAccountStatus.never');
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return t('managementAccountStatus.never');
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffInMinutes < 0) return t('managementAudit.justNow');
    if (diffInMinutes < 60) return t('managementAudit.minutesAgo', { value: diffInMinutes });
    if (diffInMinutes < 1440) return t('managementAudit.hoursAgo', { value: Math.floor(diffInMinutes / 60) });
    return t('managementAudit.daysAgo', { value: Math.floor(diffInMinutes / 1440) });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      (user.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <ToggleRight className="h-8 w-8 mr-3 text-blue-600" />
              {t('managementAccountStatus.title')}
            </h1>
            <p className="text-gray-600 mt-2">{t('managementAccountStatus.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{users.filter(u => u.is_active).length}</div>
                <div className="text-sm text-gray-500">{t('common.active')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{users.filter(u => !u.is_active).length}</div>
                <div className="text-sm text-gray-500">{t('common.inactive')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{users.filter(u => u.login_attempts > 2).length}</div>
                <div className="text-sm text-gray-500">{t('managementAccountStatus.locked')}</div>
              </div>
            </div>
            {canManageUsers && (
              <button
                onClick={openCreate}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2"
              >
                <UserPlus className="h-5 w-5" />
                <span>{t('managementUserProfile.addUser')}</span>
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-700 font-medium">{error}</span>
          </div>
        )}

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { title: 'Total Accounts', value: users.length, icon: <Users className="h-6 w-6" />, color: 'bg-blue-500', textColor: 'text-blue-600' },
            { title: 'Active Users', value: users.filter(u => u.is_active).length, icon: <CheckCircle className="h-6 w-6" />, color: 'bg-green-500', textColor: 'text-green-600' },
            { title: 'Suspended', value: users.filter(u => !u.is_active).length, icon: <AlertCircle className="h-6 w-6" />, color: 'bg-red-500', textColor: 'text-red-600' },
            { title: 'Failed Logins', value: users.reduce((sum, u) => sum + u.login_attempts, 0), icon: <Shield className="h-6 w-6" />, color: 'bg-yellow-500', textColor: 'text-yellow-600' }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">{stat.title}</p>
                  <p className={`text-3xl font-bold ${stat.textColor} mt-1`}>{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color} text-white`}>
                  {stat.icon}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent" 
                    placeholder={t('managementAccountStatus.searchPlaceholder')}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <select 
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">{t('managementAccountStatus.allStatus')}</option>
                  <option value="active">{t('managementAccountStatus.activeOnly')}</option>
                  <option value="inactive">{t('managementAccountStatus.inactiveOnly')}</option>
                </select>
                <select 
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">{t('managementAccountStatus.allRoles')}</option>
                  {USER_ROLES.map(r => (
                    <option key={r.value} value={r.value}>{t(r.labelKey)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Shield className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">{t('managementAccountStatus.accountManagement')}</h2>
              </div>
              <div className="text-sm text-gray-500">
                {t('managementAccountStatus.accountsFound', { value: filteredUsers.length })}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              <p className="text-gray-500 mt-2">{t('managementUserProfile.loading')}</p>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementAccountStatus.colUserAccount')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementAccountStatus.colRolePermissions')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementAccountStatus.colLastActivity')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementUserProfile.accountStatus')}</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center text-white font-medium">
                          {(u.username || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{u.username}</div>
                          <div className="text-sm text-gray-500">{u.email || t('managementUserProfile.noEmail')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(u.role)}`}>
                        {getRoleIcon(u.role)}
                        <span className="ml-1">{getRoleLabel(u.role)}</span>
                      </div>
                      {u.created_at && (
                        <div className="text-xs text-gray-500 mt-1">{t('managementAccountStatus.since', { value: u.created_at })}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <Clock className="h-4 w-4 text-gray-400 mr-2" />
                        {getTimeAgo(u.last_login)}
                      </div>
                      {u.login_attempts > 0 && (
                        <div className="text-xs text-red-600 mt-1">
                          {t('managementAccountStatus.failedAttempts', { value: u.login_attempts })}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {u.is_active ? (
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {t('managementAccountStatus.statusActive')}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-red-400 rounded-full mr-2"></div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {t('managementAccountStatus.statusSuspended')}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => toggle(u.id)} 
                          className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${u.is_active 
                            ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' 
                            : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                          }`}
                        >
                          {u.is_active ? (
                            <>
                              <UserX className="h-4 w-4 mr-1"/>
                              {t('managementAccountStatus.suspend')}
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-4 w-4 mr-1"/>
                              {t('managementAccountStatus.activate')}
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      {t('managementUserProfile.noUsers')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {t('managementAccountStatus.changesImmediate')}
              </div>
              <div className="flex items-center space-x-4">
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center">
                  <Zap className="h-4 w-4 mr-1" />
                  {t('managementAccountStatus.bulkActions')}
                </button>
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                  {t('managementAccountStatus.exportReport')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-6 bg-gradient-to-r from-amber-50 to-orange-100 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start">
            <Shield className="h-6 w-6 text-amber-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-amber-800 mb-2">{t('managementAccountStatus.securityNotice')}</h3>
              <div className="space-y-2 text-sm text-amber-700">
                <p>{t('managementAccountStatus.notice1')}</p>
                <p>{t('managementAccountStatus.notice2')}</p>
                <p>{t('managementAccountStatus.notice3')}</p>
                <p>{t('managementAccountStatus.notice4')}</p>
                <p>{t('managementAccountStatus.notice5')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Add User Modal (admin only) */}
        {canManageUsers && showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg mr-3">
                      <UserPlus className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{t('managementUserProfile.addNewUser')}</h3>
                      <p className="text-gray-500 text-sm">{t('managementAccountStatus.adminRequired')}</p>
                    </div>
                  </div>
                  <button
                    onClick={closeForm}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('managementUserProfile.usernameLabel')}</label>
                    <div className="relative">
                      <User className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        value={form.username}
                        onChange={e => setForm({ ...form, username: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder={t('managementUserProfile.enterUsername')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('managementUserProfile.fullName')}</label>
                    <input
                      value={form.full_name}
                      onChange={e => setForm({ ...form, full_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('managementUserProfile.enterFullName')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('managementUserProfile.emailAddress')}</label>
                    <div className="relative">
                      <Mail className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="email"
                        value={form.email}
                        onChange={e => setForm({ ...form, email: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder={t('managementUserProfile.enterEmail')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('managementUserProfile.userRole')}</label>
                    <select
                      value={form.role}
                      onChange={e => setForm({ ...form, role: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      {USER_ROLES.map(r => (
                        <option key={r.value} value={r.value}>{t(r.labelKey)}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('managementAccountStatus.passwordLabel')}</label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('managementUserProfile.enterPassword')}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{t('managementUserProfile.accountStatus')}</div>
                        <div className="text-sm text-gray-500">{t('managementAccountStatus.createHint')}</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.is_active}
                          onChange={e => setForm({ ...form, is_active: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end space-x-3">
                <button
                  onClick={closeForm}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={createUser}
                  disabled={saving}
                  className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4" />
                  <span>{saving ? t('managementAccountStatus.creating') : t('managementUserProfile.createUser')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ManagementAccountStatus;
